import React, { useEffect, useRef, useState } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { Card } from '@sunwisesoftware/sunwise-ui';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';

import CustomPagination from 'common/components/CustomPagination';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';
import CustomColumns from 'common/modules/customColumnsV2';
import { getOrderedColumns, getHiddenColumns } from 'common/utils/helpers';

import { COLUMNS_CONFIG } from '../constants';

import BulkOperationsCard from './BulkOperationsCard';
import Columns from './Columns';

const ListItems = ({
    companies,
    downloadFile,
    filterData,
    filterItems,
    isFetching,
    items,
    paginationData,
    rates,
    setFilterData,
    tableSettings,
}) => {
    const [rowCount, setRowCount] = useState(paginationData?.rowCount || 0);

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeSearch = (event) => {
        setFilterData({
            ...filterData,
            page: 0,
            searchText: event.target.value,
        });
    };

    const handleOnSelectCompany = (value) => {
        const filter = {
            ...filterData,
            company: value,
            page: 0,
        };
        setFilterData(filter);
    };

    const handleOnSelectStatus = (value) => {
        const filter = { ...filterData, page: 0, status: value };
        setFilterData(filter);
    };

    const handleOnSelectRate = (item) => {
        setFilterData({
            ...filterData,
            rateName: item.value,
        });
    };

    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;

    const columns = Columns({
        hiddenColumns: getHiddenColumns(tableSettings),
        orders: getOrderedColumns(tableSettings),
    });

    const columnVisibilityModel = columns.reduce(
        (acc, column) => ({ ...acc, [column.field]: !column.hide }),
        {},
    );

    const getRowHeight = () => 'auto';
    const initialState = {
        pagination: {
            paginationModel: { pageSize: TABLE_PAGE_SIZE_OPTIONS[0] },
        },
    };
    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));
    const onSortModelChange = (sortModel) => {
        if (!sortModel[0]) return;
        setFilterData((prev) => ({
            ...prev,
            orderBy: sortModel[0].field.toLocaleLowerCase(),
            sortBy: sortModel[0].sort,
        }));
    };

    const slots = { pagination: CustomPagination };
    const slotProps = {
        pagination: {
            customContent: (
                <CustomColumns.Container
                    columnsConfig={COLUMNS_CONFIG}
                    tableKey="quotes"
                />
            ),
            pageSizeOptions,
        },
    };

    useEffect(() => {
        if (paginationData)
            setRowCount((prevRowCount) =>
                paginationData?.total_rows !== undefined
                    ? paginationData?.total_rows
                    : prevRowCount,
            );
    }, [paginationData?.total_rows, setRowCount]);

    useEffect(() => {
        handleOnFilter(filterData);
    }, [filterData]);

    return (
        <>
            <BulkOperationsCard
                companies={companies}
                downloadFile={downloadFile}
                filterData={filterData}
                handleOnChangeSearch={handleOnChangeSearch}
                handleOnSelectCompany={handleOnSelectCompany}
                handleOnSelectStatus={handleOnSelectStatus}
                handleOnSelectRate={handleOnSelectRate}
                isFetching={isFetching}
                rates={rates}
            />
            <Card>
                <Card.Body>
                    <DataGrid
                        columnVisibilityModel={columnVisibilityModel}
                        columns={columns}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        getRowHeight={getRowHeight}
                        initialState={initialState}
                        loading={isFetching}
                        onPaginationModelChange={onPaginationModelChange}
                        onSortModelChange={onSortModelChange}
                        pagination
                        paginationMode="server"
                        rowCount={rowCount}
                        rows={items}
                        slotProps={slotProps}
                        slots={slots}
                        sortingMode="server"
                    />
                </Card.Body>
            </Card>
        </>
    );
};

ListItems.propTypes = {
    companies: PropTypes.array,
    downloadFile: PropTypes.func,
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    isFetching: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    rates: PropTypes.array,
    setFilterData: PropTypes.func,
    tableSettings: PropTypes.array,
};

export default ListItems;
